import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { startOfMonth, endOfMonth } from 'date-fns';
import { INITIAL_DEMO_TRANSACTIONS } from '../lib/demoData';

const TransactionContext = createContext();

export const useTransactions = () => {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error('useTransactions must be used within a TransactionProvider');
  }
  return context;
};

const DEMO_TX_KEY = 'spend_easy_demo_transactions';

const loadDemoTransactions = () => {
  try {
    const saved = localStorage.getItem(DEMO_TX_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length >= 80) {
        return parsed.map(t => ({
          ...t,
          date: new Date(t.date)
        }));
      }
    }
  } catch (e) {
    console.error('Error loading demo transactions:', e);
  }
  saveDemoTransactions(INITIAL_DEMO_TRANSACTIONS);
  return INITIAL_DEMO_TRANSACTIONS;
};

const saveDemoTransactions = (txs) => {
  try {
    localStorage.setItem(DEMO_TX_KEY, JSON.stringify(txs));
  } catch (e) {
    console.error('Error saving demo transactions:', e);
  }
};

export const TransactionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setTransactions([]);
      setLoading(false);
      return;
    }

    if (currentUser.isGuest) {
      const updateGuestData = () => {
        setTransactions(loadDemoTransactions());
      };
      updateGuestData();
      setLoading(false);
      window.addEventListener('spend_easy_demo_data_updated', updateGuestData);
      return () => {
        window.removeEventListener('spend_easy_demo_data_updated', updateGuestData);
      };
    }

    const transactionsRef = collection(db, 'users', currentUser.uid, 'transactions');
    const q = query(transactionsRef, orderBy('date', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const transactionsList = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          date: data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date)
        };
      });
      setTransactions(transactionsList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching transactions:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const addTransaction = async (transactionData) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const newTx = {
        ...transactionData,
        id: `demo-tx-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        userId: currentUser.uid,
        date: new Date(transactionData.date),
        createdAt: new Date()
      };
      const updated = [newTx, ...transactions];
      setTransactions(updated);
      saveDemoTransactions(updated);
      return { id: newTx.id };
    }

    const transactionsRef = collection(db, 'users', currentUser.uid, 'transactions');
    const docData = {
      ...transactionData,
      userId: currentUser.uid,
      date: Timestamp.fromDate(transactionData.date),
      createdAt: serverTimestamp()
    };
    const docRef = await addDoc(transactionsRef, docData);
    return docRef;
  };

  const updateTransaction = async (transactionId, transactionData) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = transactions.map(t => {
        if (t.id === transactionId) {
          return {
            ...t,
            ...transactionData,
            date: new Date(transactionData.date || t.date),
            updatedAt: new Date()
          };
        }
        return t;
      });
      setTransactions(updated);
      saveDemoTransactions(updated);
      return;
    }

    const transactionRef = doc(db, 'users', currentUser.uid, 'transactions', transactionId);
    await updateDoc(transactionRef, {
      ...transactionData,
      date: Timestamp.fromDate(transactionData.date),
      updatedAt: serverTimestamp()
    });
  };

  const deleteTransaction = async (transactionId) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = transactions.filter(t => t.id !== transactionId);
      setTransactions(updated);
      saveDemoTransactions(updated);
      return;
    }

    const transactionRef = doc(db, 'users', currentUser.uid, 'transactions', transactionId);
    await deleteDoc(transactionRef);
  };

  const getFilteredTransactions = () => {
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);
    return transactions.filter(t => {
      const transactionDate = t.date;
      return transactionDate >= monthStart && transactionDate <= monthEnd;
    });
  };

  const getSummary = () => {
    const filtered = getFilteredTransactions();
    const income = filtered.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const expense = filtered.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
    return { income, expense, total: income - expense };
  };

  const value = {
    transactions: getFilteredTransactions(),
    allTransactions: transactions,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    selectedMonth,
    setSelectedMonth,
    loading,
    summary: getSummary()
  };

  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  );
};
